import type { GetServerSideProps, NextPage } from 'next';
import { z } from 'zod';
import Script from 'next/script';
import { getApolloSsrClient, getVisitorId } from '@stitch-fix/sf-next/server';
import { useEffect } from 'react';
import { fireEvent } from '@stitch-fix/kufak-gtm-client';
import { getCachedEntry } from '../utils/requests/contentstack/cachedRequests';
import { formatError, serverLogger } from '../utils/loggers/server';
import { modularPageWrapperSchema } from '../components/ModularPageWrapper/schema';
import { ModularPageWrapper } from '../components/ModularPageWrapper';
import { isAllSettledResponseFailure } from '../utils/promises';
import { getDynamicAdsRequest } from '../features/dynamicAds/utils/getDynamicAdsRequest';
import { getModulesWithHeroOverride } from '../features/dynamicAds/utils/getModulesWithHeroOverride';
import { setOfferOverride } from '../utils/modules/setOfferOverride';
import { getContentVariant, getContentstackEntryUid } from '../utils/getContentVariant/getContentVariant';
import { allocateVisitorSsr } from '../utils/allocateVisitorSsr/allocateVisitorSsr';
import { GATEWAY_BRANDS_VALUE_REDESIGN_CONFIG_PARAM_NAME, GATEWAY_BRANDS_VALUE_REDESIGN_CONFIG_PARAMS, GATEWAY_BRANDS_VALUE_REDESIGN_CONTROL, GATEWAY_BRANDS_VALUE_REDESIGN_TREATMENT, isGatewayBrandsValueRedesignTreatment } from '../experiments/gatewayBrandsValueRedesign';
import BrandsValuePage from './marketing-pages/brands-value/index.page';
import { isDynamicAdsLandingPage, isSUPLandingPage } from '../utils/redirects';
import { addFeatureFlagsToRumContext } from '../utils/loggers/browser';
import { type GatewayHeroLayoutAllocationValue, getGatewayHeroLayoutAllocation, overrideHeroModuleByGatewayHeroLayout } from '../experiments/gatewayHeroLayout';
import { GATEWAY_HERO_LAYOUT_CONFIG_PARAM_NAME } from '../utils/helpers';
type GatewayPageProps = z.infer<typeof modularPageWrapperSchema> & {
  pageVariant: 'control' | 'brands-value-redesign';
  gatewayHeroLayoutAllocationValue?: GatewayHeroLayoutAllocationValue;
};
const GATEWAY_ENTRY_UIDS = {
  supTarget: 'blt4673ca40ee70b8ab',
  q3Redesign: 'blt77c77eb8f8599f0e'
};
const shouldAllocateGatewayBrandsValueRedesign = (pathname: string) => !isDynamicAdsLandingPage(pathname) && !isSUPLandingPage(pathname);
export const getServerSideProps: GetServerSideProps<GatewayPageProps> = async ({
  req,
  query
}) => {
  const url = req.url || '';
  const pathname = new URL(url, 'https://www.stitchfix.com').pathname;
  try {
    const apolloSsrClient = getApolloSsrClient({
      req
    });
    const dynamicAdsRequest = getDynamicAdsRequest({
      req,
      apolloSsrClient
    });
    const gatewayHeroLayoutAllocation = await getGatewayHeroLayoutAllocation({
      apolloSsrClient,
      query,
      req,
      url
    });
    const contentVariant = getContentVariant({
      url
    });
    const entryRequest = getCachedEntry({
      contentType: 'modular_page',
      uid: getContentstackEntryUid({
        contentVariant,
        uids: GATEWAY_ENTRY_UIDS
      }),
      schema: modularPageWrapperSchema,
      logger: serverLogger
    });
    const responses = await Promise.allSettled([entryRequest, dynamicAdsRequest]);
    const [entryResponse, dynamicAdsResponse] = responses;
    const dynamicAdsData = dynamicAdsResponse.status === 'fulfilled' ? dynamicAdsResponse.value?.data : undefined;
    if (isAllSettledResponseFailure(entryResponse)) {
      throw entryResponse.reason;
    }
    const modules = entryResponse.value.data.modules;
    const heroModules = getModulesWithHeroOverride({
      businessLinePage: 'gateway',
      modules,
      dynamicAds: dynamicAdsData,
      dynamicAdHeroContent: entryResponse.value.data.dynamicAdHeroContent
    });
    let pageVariant: 'control' | 'brands-value-redesign' = 'control';
    if (shouldAllocateGatewayBrandsValueRedesign(pathname)) {
      const allocationResult = await allocateVisitorSsr({
        req,
        visitorId: getVisitorId(req),
        apolloSsrClient,
        offlineLookupOnly: false,
        configParams: GATEWAY_BRANDS_VALUE_REDESIGN_CONFIG_PARAMS,
        onError: error => {
          serverLogger({
            level: 'error',
            team: 'client-onboarding',
            feature: 'gateway',
            message: 'Failed to fetch visitor allocation value for Gateway Brands Value Redesign experiment',
            context: {
              error: formatError(error),
              page: url
            }
          });
        }
      });
      pageVariant = isGatewayBrandsValueRedesignTreatment(allocationResult.data?.visitor?.configParams) ? 'brands-value-redesign' : 'control';
    }
    const modulesWithOfferOverride = setOfferOverride(heroModules);
    const modulesWithHeroLayoutOverride = pageVariant === 'control' ? await overrideHeroModuleByGatewayHeroLayout({
      allocationValue: gatewayHeroLayoutAllocation,
      modules: modulesWithOfferOverride
    }) : modulesWithOfferOverride;
    return {
      props: {
        seoData: entryResponse.value.data.seoData,
        modules: modulesWithHeroLayoutOverride,
        pageVariant,
        gatewayHeroLayoutAllocationValue: gatewayHeroLayoutAllocation
      }
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Required page data is undefined - Gateway',
      context: {
        error: formatError(error),
        page: url,
        severity: 'critical'
      }
    });
    throw new Error('Required data not provided to render Gateway page');
  }
};
const GatewayPage: NextPage<GatewayPageProps> = ({
  pageVariant,
  seoData,
  modules,
  gatewayHeroLayoutAllocationValue = 'control'
}) => {
  useEffect(() => {
    // Send GTM event with expt number and allocation value to allow easy detection in FullStory
    fireEvent({
      eventName: 'expt_1762',
      eventAttributes: {
        allocationValue: pageVariant === 'brands-value-redesign' ? GATEWAY_BRANDS_VALUE_REDESIGN_TREATMENT : GATEWAY_BRANDS_VALUE_REDESIGN_CONTROL
      }
    });
    // Add feature flag to RUM context
    addFeatureFlagsToRumContext({
      [GATEWAY_BRANDS_VALUE_REDESIGN_CONFIG_PARAM_NAME]: pageVariant === 'brands-value-redesign' ? GATEWAY_BRANDS_VALUE_REDESIGN_TREATMENT : GATEWAY_BRANDS_VALUE_REDESIGN_CONTROL
    });
    addFeatureFlagsToRumContext({
      [GATEWAY_HERO_LAYOUT_CONFIG_PARAM_NAME]: gatewayHeroLayoutAllocationValue
    });
    fireEvent({
      eventName: 'expt_1814',
      eventAttributes: {
        allocationValue: gatewayHeroLayoutAllocationValue
      }
    });
  }, [pageVariant, gatewayHeroLayoutAllocationValue]);
  return <>
      <Script
    // Blueshift web conversion integration (https://experiences.getblueshift.com/)
    data-testid="blueshift-script" src="https://www.lightboxcdn.com/vendor/3767d51a-4af9-4270-8ee4-c7310e4371b0/lightbox_speed.js" defer />
      {pageVariant === 'brands-value-redesign' ? <BrandsValuePage seoData={seoData} /> : <ModularPageWrapper seoData={seoData} modules={modules} />}
    </>;
};
export default GatewayPage;